import React, { useState, useEffect, useRef } from "react";
import * as charitiesLib from "../lib/charitiesLib";
import { Inbox } from "../lib/inboxLib";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface CharityPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inboxId: string, updateData: Partial<Inbox>) => Promise<void>;
  inbox: Inbox;
}

const CharityPicker: React.FC<CharityPickerProps> = ({
  isOpen,
  onClose,
  onSave,
  inbox,
}) => {
  const [charities, setCharities] = useState<charitiesLib.Charity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCharity, setSelectedCharity] =
    useState<charitiesLib.Charity | null>(inbox.charity);
  const [currentPage, setCurrentPage] = useState(0);
  const charitiesPerPage = 3;
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCharities = async () => {
      try {
        setLoading(true);
        const data = await charitiesLib.getAll();
        setCharities(data);
      } catch (error) {
        console.error("Failed to fetch charities:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchCharities();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (!selectedCharity) return;
    await onSave(inbox.id, { charity: selectedCharity });
    onClose();
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) =>
      Math.min(Math.ceil(charities.length / charitiesPerPage) - 1, prev + 1),
    );
  };

  if (!isOpen) return null;

  const displayedCharities = charities.slice(
    currentPage * charitiesPerPage,
    (currentPage + 1) * charitiesPerPage,
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-4xl"
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          Select Charity
        </h2>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Charity Carousel */}
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {displayedCharities.map((charity) => (
                  <div
                    key={charity.id}
                    className={`bg-white dark:bg-gray-700 rounded-lg shadow-md overflow-hidden cursor-pointer transition-all ${
                      selectedCharity?.id === charity.id
                        ? "ring-2 ring-blue-500"
                        : ""
                    }`}
                    onClick={() => setSelectedCharity(charity)}
                  >
                    <div className="h-32 bg-gray-200 dark:bg-gray-600 relative">
                      {charity.heroUrl ? (
                        <img
                          src={charity.heroUrl}
                          alt={`${charity.name} banner`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <span className="text-gray-400 dark:text-gray-500">
                            No hero image
                          </span>
                        </div>
                      )}
                      {charity.logoUrl && (
                        <div className="absolute bottom-2 left-2 h-16 w-16 rounded-full bg-white dark:bg-gray-800 p-1 shadow">
                          <img
                            src={charity.logoUrl}
                            alt={`${charity.name} logo`}
                            className="w-full h-full object-contain rounded-full"
                          />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-1 dark:text-white">
                        {charity.name}
                      </h3>
                      {charity.blurb && (
                        <p className="text-gray-700 dark:text-gray-300 text-sm line-clamp-2">
                          {charity.blurb}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              {charities.length > charitiesPerPage && (
                <>
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 0}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white dark:bg-gray-700 p-2 rounded-full shadow hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaChevronLeft className="text-gray-600 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={
                      currentPage >=
                      Math.ceil(charities.length / charitiesPerPage) - 1
                    }
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white dark:bg-gray-700 p-2 rounded-full shadow hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaChevronRight className="text-gray-600 dark:text-gray-300" />
                  </button>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded hover:bg-gray-400 dark:hover:bg-gray-600 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedCharity}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharityPicker;
